import { useEffect, useMemo, useState } from 'react';
import { Check, Download, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/Toast';
import { invoicesService } from '@/services/invoices.service';
import { exportInvoicesToZip, ExportProgress } from '@/lib/invoice-export';
import type { Invoice, InvoiceStatus } from '@/types';
import { cn } from '@/lib/utils';

interface ExportInvoicesModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const STATUS_OPTIONS: { value: InvoiceStatus; label: string }[] = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'paid', label: 'Paid' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'cancelled', label: 'Cancelled' },
];

export function ExportInvoicesModal({ open, onOpenChange }: ExportInvoicesModalProps) {
  const { toast } = useToast();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<InvoiceStatus>>(new Set());
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState<ExportProgress | null>(null);

  // Load all invoices when the modal opens so counts are accurate and
  // independent of the page's active filters.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadError(null);
    setProgress(null);
    setSelected(new Set());
    (async () => {
      try {
        setLoading(true);
        const data = await invoicesService.getInvoices();
        if (!cancelled) setInvoices(data);
      } catch (err: any) {
        if (!cancelled) setLoadError(err?.message || 'Failed to load invoices');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const countsByStatus = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const invoice of invoices) {
      counts[invoice.status] = (counts[invoice.status] || 0) + 1;
    }
    return counts;
  }, [invoices]);

  const selectedInvoices = useMemo(
    () => invoices.filter((inv) => selected.has(inv.status)),
    [invoices, selected]
  );

  const allSelectableStatuses = STATUS_OPTIONS.filter(
    (opt) => (countsByStatus[opt.value] || 0) > 0
  ).map((opt) => opt.value);

  const allSelected =
    allSelectableStatuses.length > 0 &&
    allSelectableStatuses.every((status) => selected.has(status));

  const toggleStatus = (status: InvoiceStatus) => {
    if ((countsByStatus[status] || 0) === 0) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(allSelectableStatuses));
  };

  const handleExport = async () => {
    if (selectedInvoices.length === 0) return;
    try {
      setExporting(true);
      setProgress({ completed: 0, total: selectedInvoices.length });

      const date = new Date().toISOString().split('T')[0];
      const result = await exportInvoicesToZip(selectedInvoices, {
        fileName: `invoices-${date}.zip`,
        onProgress: setProgress,
      });

      if (result.failed.length === 0) {
        toast.success(
          `Exported ${result.succeeded.length} invoice${result.succeeded.length === 1 ? '' : 's'}`
        );
        onOpenChange(false);
      } else if (result.succeeded.length > 0) {
        toast.warning(
          `Exported ${result.succeeded.length}, but ${result.failed.length} failed`
        );
      } else {
        toast.error('Failed to export invoices');
      }
    } catch (err: any) {
      console.error('Error exporting invoices:', err);
      toast.error(err?.message || 'Failed to export invoices');
    } finally {
      setExporting(false);
      setProgress(null);
    }
  };

  const progressPercent =
    progress && progress.total > 0
      ? Math.round((progress.completed / progress.total) * 100)
      : 0;

  return (
    <Dialog open={open} onOpenChange={(next) => !exporting && onOpenChange(next)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export Invoices</DialogTitle>
          <DialogDescription>
            Select the invoice statuses to include. The selected invoices will be
            downloaded as PDFs bundled in a ZIP file.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading invoices...
          </div>
        ) : loadError ? (
          <div className="py-6 text-sm text-destructive">{loadError}</div>
        ) : (
          <div className="space-y-2 py-2">
            <button
              type="button"
              onClick={toggleAll}
              disabled={exporting || allSelectableStatuses.length === 0}
              className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
            >
              <span>{allSelected ? 'Deselect all' : 'Select all'}</span>
            </button>

            {STATUS_OPTIONS.map((opt) => {
              const count = countsByStatus[opt.value] || 0;
              const isChecked = selected.has(opt.value);
              const disabled = exporting || count === 0;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleStatus(opt.value)}
                  disabled={disabled}
                  className={cn(
                    'flex w-full items-center justify-between rounded-md border px-3 py-2.5 text-left transition-colors',
                    isChecked ? 'border-primary bg-primary/5' : 'border-border',
                    disabled
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:bg-muted cursor-pointer'
                  )}
                >
                  <span className="flex items-center gap-3">
                    <span
                      className={cn(
                        'flex h-4 w-4 items-center justify-center rounded border',
                        isChecked
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-muted-foreground/40'
                      )}
                    >
                      {isChecked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="text-sm font-medium text-foreground">{opt.label}</span>
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {count} invoice{count === 1 ? '' : 's'}
                  </span>
                </button>
              );
            })}

            {invoices.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No invoices available to export.
              </p>
            )}
          </div>
        )}

        {exporting && progress && (
          <div className="space-y-2">
            <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Generating PDFs… {progress.completed}/{progress.total}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={exporting || selectedInvoices.length === 0}>
            {exporting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Exporting…
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export {selectedInvoices.length > 0 ? `(${selectedInvoices.length})` : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
